import React, { useState } from "react";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { googlePlacesService } from "../../../../../services/googlePlacesService";
import { GooglePlaceWithDistance } from "../../../../../types/googlePlaces";
import { Shop } from "../../../../../types/shop";
import { ShopCard } from "./ShopCard";
import {
  ShopDetailsDialog,
  ShopDetailsDialogData,
} from "./ShopDetailsDialog";
import { useGooglePlaceDetails } from "../../../hooks/useGooglePlaceDetails";
import {
  convertGooglePlaceToShop,
  shortenTime,
} from "../../../../../utils/shopUtils";

// Constants for search configuration
const KEYWORDS = [
  "Heavy duty truck repair shop",
  "Heavy duty mobile repair",
  "Heavy duty towing",
];
const SEARCH_RADIUS_METERS = 50000; // 50km radius
const INITIAL_RESULTS = 15; // Get more initially, then take top 10
const TOP_RESULTS = 10; // Final number of results to display

interface GoogleShopsListProps {
  selectedVehicle: {
    lastLatitude?: number;
    lastLongitude?: number;
  };
  onShopSelect?: (shops: Shop | Shop[]) => void; // Can handle single or multiple shops
  onCenterShop?: (shop: any) => void;
  onPanOut?: () => void;
  onAddShop?: (shop: Shop, preference: number) => void;
  // Lifted state props
  googleShops: GooglePlaceWithDistance[];
  setGoogleShops: React.Dispatch<
    React.SetStateAction<GooglePlaceWithDistance[]>
  >;
  convertedShops: Shop[];
  setConvertedShops: React.Dispatch<React.SetStateAction<Shop[]>>;
  isGoogleLoading: boolean;
  setIsGoogleLoading: React.Dispatch<React.SetStateAction<boolean>>;
  googleError: string | null;
  setGoogleError: React.Dispatch<React.SetStateAction<string | null>>;
  hasSearched: boolean;
  setHasSearched: React.Dispatch<React.SetStateAction<boolean>>;
}

export const GoogleShopsList: React.FC<GoogleShopsListProps> = ({
  selectedVehicle,
  onShopSelect,
  onCenterShop,
  onPanOut,
  onAddShop,
  // Lifted state props
  googleShops,
  setGoogleShops,
  convertedShops,
  setConvertedShops,
  isGoogleLoading,
  setIsGoogleLoading,
  googleError,
  setGoogleError,
  hasSearched,
  setHasSearched,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [detailsDialog, setDetailsDialog] =
    useState<ShopDetailsDialogData | null>(null);
  const { fetchGooglePlaceDetails, isFetchingDetails } =
    useGooglePlaceDetails();

  const handleSearch = async (keyword: string) => {
    if (!selectedVehicle.lastLatitude || !selectedVehicle.lastLongitude) {
      setGoogleError("Vehicle location not available");
      return;
    }

    setIsGoogleLoading(true);
    setGoogleError(null);
    setHasSearched(true);

    try {
      console.log("🔍 Searching Google Places:", {
        keyword,
        location: `${selectedVehicle.lastLatitude},${selectedVehicle.lastLongitude}`,
        radius: SEARCH_RADIUS_METERS,
      });

      // Calculate distances and get top results
      const placesWithDistance =
        await googlePlacesService.getPlacesSortedByDistance(
          {
            location: `${selectedVehicle.lastLatitude},${selectedVehicle.lastLongitude}`,
            radius: SEARCH_RADIUS_METERS,
            keyword: keyword,
            maxResults: INITIAL_RESULTS,
          },
          selectedVehicle.lastLatitude,
          selectedVehicle.lastLongitude
        );

      console.log(
        `📍 Found ${placesWithDistance.length} places for "${keyword}"`
      );

      if (placesWithDistance.length === 0) {
        setGoogleShops([]);
        return;
      }

      // Take top results
      const topResults = placesWithDistance.slice(0, TOP_RESULTS);

      setGoogleShops(topResults);

      // Convert to Shop format for compatibility using centralized utility
      const converted = topResults.map((place) =>
        convertGooglePlaceToShop(place)
      );

      setConvertedShops(converted);

      // Notify parent component about the Google shops for map display
      if (onShopSelect && converted.length > 0) {
        onShopSelect(converted);
      }

      console.log(
        `✅ Google Places search completed: ${topResults.length} results`
      );
    } catch (err) {
      console.error("❌ Google Places search failed:", err);
      setGoogleError(err instanceof Error ? err.message : "Search failed");
      setGoogleShops([]);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleKeywordClick = (keyword: string) => {
    setSearchQuery(keyword);
    handleSearch(keyword);
  };

  const handleCustomSearch = () => {
    if (searchQuery.trim()) {
      handleSearch(searchQuery.trim());
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setGoogleShops([]);
    setConvertedShops([]);
    setGoogleError(null);
    setHasSearched(false);
  };

  const handleShowDetails = async (shop: Shop): Promise<Shop | undefined> => {
    try {
      const detailedShop = await fetchGooglePlaceDetails(shop);
      if (detailedShop) {
        // Update the converted shops with detailed information
        setConvertedShops((prev) =>
          prev.map((s) => (s.placeId === shop.placeId ? detailedShop : s))
        );
        return detailedShop;
      }
    } catch (error) {
      console.error("Error fetching shop details:", error);
    }
    return undefined;
  };

  const handleAddShop = (shop: Shop, preference: number) => {
    // This will be handled by the parent component to open the modal
    // We'll pass it through the callback
    if (onAddShop) {
      onAddShop(shop, preference);
    }
  };

  return (
    <Box>
      {/* Search Interface - Always visible */}
      <Box sx={{ mb: 1 }}>
        {/* Search Input and Keywords in one row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 1,
            flexWrap: "wrap",
          }}
        >
          {/* Search Input */}
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <TextField
              size="small"
              placeholder="Search for repair shops..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleCustomSearch();
                }
              }}
              sx={{
                width: 220,
                "& .MuiInputBase-root": {
                  height: 32,
                  fontSize: "0.75rem",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleClearSearch}
                      size="small"
                      sx={{ p: 0.5 }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <IconButton
              onClick={handleCustomSearch}
              disabled={!searchQuery.trim() || isGoogleLoading}
              size="small"
              sx={{
                bgcolor: "primary.main",
                color: "white",
                borderRadius: "50%",
                width: 32,
                height: 32,
                "&:hover": { bgcolor: "primary.dark" },
                "&:disabled": { bgcolor: "action.disabled" },
              }}
            >
              <SearchIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {/* Predefined Keywords */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              alignItems: "center",
            }}
          >
            {KEYWORDS.map((keyword, index) => (
              <Chip
                key={index}
                label={keyword}
                size="small"
                variant="outlined"
                clickable
                onClick={() => handleKeywordClick(keyword)}
                sx={{
                  fontSize: "0.7rem",
                  height: 24,
                  "&:hover": {
                    bgcolor: "primary.light",
                    color: "primary.contrastText",
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Results Area */}
      {googleError ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {googleError}
        </Alert>
      ) : !hasSearched ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 3,
            textAlign: "center",
            bgcolor: "background.default",
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
          }}
        >
          <SearchIcon sx={{ fontSize: 32, color: "text.disabled", mb: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Search Google Places
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Enter a search term or click a quick search above
          </Typography>
        </Box>
      ) : isGoogleLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 3,
            gap: 1,
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Searching Google Places...
          </Typography>
        </Box>
      ) : googleShops.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 3,
            textAlign: "center",
          }}
        >
          <SearchIcon sx={{ fontSize: 32, color: "text.disabled", mb: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            No Results Found
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Try a different search term or location
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ maxHeight: 220, overflowY: "auto", pr: 0.5, pb: 0.5 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              {googleShops.map((shop) => {
                // Find the corresponding converted shop with detailed info
                const convertedShop = convertedShops.find(
                  (s) => s.placeId === shop.place_id
                );
                return (
                  <ShopCard
                    key={shop.place_id}
                    data={{
                      type: "google",
                      shop: shop,
                      convertedShop: convertedShop,
                    }}
                    onCenterShop={(shopData) => {
                      if (onCenterShop) {
                        // Try to find in convertedShops first, but fallback to converting on the fly
                        let convertedShop = convertedShops.find(
                          (s) => s.placeId === shopData.place_id
                        );

                        // If not found in convertedShops, convert the shop data on the fly using centralized utility
                        if (!convertedShop) {
                          convertedShop = convertGooglePlaceToShop(shopData);
                        }

                        if (convertedShop) {
                          onCenterShop(convertedShop);
                        }
                      }
                    }}
                    onShowDetails={handleShowDetails}
                    onShowDetailsDialog={setDetailsDialog}
                    onAddShop={handleAddShop}
                    shortenTime={shortenTime}
                    isFetchingDetails={isFetchingDetails}
                  />
                );
              })}
            </Box>
          </Box>
          <ShopDetailsDialog
            open={Boolean(detailsDialog)}
            details={detailsDialog}
            onClose={() => setDetailsDialog(null)}
          />
        </>
      )}
    </Box>
  );
};
